import type * as React from 'react';

import * as SheetPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { twSort } from '@/_shared';
import type { DivProps } from '@/types';

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay> & {
  ref?: React.RefObject<React.ElementRef<typeof SheetPrimitive.Overlay> | null>;
}) => (
  <SheetPrimitive.Overlay
    className={twMerge(
      'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 dark:bg-white/05 bg-gray-950/05 fixed inset-0 z-30 transform-gpu',
      className
    )}
    {...props}
    ref={ref}
  />
);

const defaultStyle = twSort`border-c-border data-[state=open]:animate-in data-[state=closed]:animate-out sheet-shadow fixed z-50 h-dvh transform-gpu gap-4 bg-white p-6 transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 dark:bg-gray-950`;
const sides = {
  top: twSort`data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 border-b`,
  bottom: twSort`data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 border-t`,
  left: twSort`data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-56 border-r sm:max-w-sm`,
  right: twSort`data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm`,
};

type SheetVariants = 'top' | 'bottom' | 'left' | 'right';

interface SheetContentProps extends React.ComponentProps<typeof SheetPrimitive.Content> {
  side?: SheetVariants;
  closeButtonClassName?: string;
  overlayClassName?: string;
  /** Render inline (no portal, no overlay) with absolute positioning — for scoping the sheet inside a container. */
  contained?: boolean;
  /** Top offset in px when `contained` is true. */
  offsetTop?: number;
}

const SheetContent = ({
  ref,
  side = 'left',
  className,
  closeButtonClassName,
  children,
  overlayClassName,
  contained = false,
  offsetTop,
  ...props
}: SheetContentProps & {
  ref?: React.RefObject<React.ElementRef<typeof SheetPrimitive.Content> | null>;
}) => {
  const content = (
    <SheetPrimitive.Content
      ref={ref}
      className={twMerge(defaultStyle, contained && 'absolute! z-10', sides[side as keyof typeof sides], className)}
      style={offsetTop !== undefined ? { top: offsetTop } : undefined}
      {...props}
    >
      <div className="hidden">
        <SheetTitle>{props.title}</SheetTitle>
      </div>
      {children}

      <SheetPrimitive.Close
        className={twMerge(
          'text-accent-x-light data-[state=open]:bg-secondary absolute top-3 right-3 flex size-10 cursor-pointer items-center justify-center rounded-full p-0 opacity-70 transition-opacity hover:opacity-100 focus:outline-hidden disabled:pointer-events-none dark:text-gray-400',
          closeButtonClassName
        )}
      >
        <X className="m-0 size-6" />
        <span className="sr-only">Close</span>
      </SheetPrimitive.Close>
    </SheetPrimitive.Content>
  );

  if (contained) {
    return content;
  }

  return (
    <SheetPortal>
      <SheetOverlay className={overlayClassName} />
      {content}
    </SheetPortal>
  );
};

function SheetHeader({ className, ...props }: DivProps) {
  return <div className={twMerge('flex flex-col space-y-2 text-center sm:text-left', className)} {...props} />;
}

function SheetFooter({ className, ...props }: DivProps) {
  return (
    <div className={twMerge('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)} {...props} />
  );
}

const SheetTitle = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title> & {
  ref?: React.RefObject<React.ElementRef<typeof SheetPrimitive.Title> | null>;
}) => (
  <SheetPrimitive.Title ref={ref} className={twMerge('text-foreground text-lg font-semibold', className)} {...props} />
);

const SheetDescription = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description> & {
  ref?: React.RefObject<React.ElementRef<typeof SheetPrimitive.Description> | null>;
}) => (
  <SheetPrimitive.Description ref={ref} className={twMerge('text-muted-foreground text-sm', className)} {...props} />
);

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
